const SECTION_ID = 'lafortune-quick-view';

class LafortuneCatalogPanel extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {AbortController | null} */
  #qvAbort = null;
  /** @type {number} */
  #slideIndex = 0;

  connectedCallback() {
    if (!document.body.classList.contains('template-collection')) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#initProductCards(signal);
    this.#initToolbar(signal);
    this.#initTyping(signal);

    this.querySelectorAll('[data-lf-panel-close]').forEach((el) => {
      el.addEventListener('click', () => this.close(), { signal });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    }, { signal });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.#closeSizeChart();
    }, { signal });

    document.addEventListener('cart:update', (e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      if (detail?.data?.source === 'product-form-component' && detail?.data?.didError !== true) {
        this.close();
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#qvAbort?.abort();
  }

  get isOpen() {
    return document.body.classList.contains('lf-catalog-open');
  }

  /** @param {AbortSignal} signal */
  #initProductCards(signal) {
    document.querySelectorAll('product-card').forEach((card) => {
      card.setAttribute('data-no-navigation', 'true');
    });

    document.addEventListener('click', (e) => {
      if (!document.body.classList.contains('template-collection')) return;

      const card = e.target.closest('product-card');
      if (!card) return;
      if (e.target.closest('button, input, label, select')) return;

      const link = card.querySelector('.product-card__link');
      if (!(link instanceof HTMLAnchorElement) || !link.href) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      this.open(link.href);
    }, { signal, capture: true });

    const grid = document.querySelector('.product-grid');
    if (grid) {
      new MutationObserver(() => {
        document.querySelectorAll('product-card:not([data-no-navigation])').forEach((card) => {
          card.setAttribute('data-no-navigation', 'true');
        });
        this.#runTyping(grid);
      }).observe(grid, { childList: true, subtree: true });
    }
  }

  /** @param {AbortSignal} signal */
  #initToolbar(signal) {
    document.querySelector('[data-lf-open-filters]')?.addEventListener('click', () => {
      this.#enhanceFilterModal();
      const drawer = document.getElementById('filters-drawer');
      if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
    }, { signal });

    document.querySelector('[data-lf-view-toggle]')?.addEventListener('click', () => {
      document.body.classList.toggle('lf-catalog-view-expanded');
    }, { signal });

    const relabel = () => this.#enhanceFilterModal();
    relabel();
    document.addEventListener('shopify:section:load', relabel, { signal });
  }

  #enhanceFilterModal() {
    const drawer = document.querySelector('#filters-drawer');
    if (!drawer) return;

    const title = drawer.querySelector('.facets-drawer__title');
    if (title) {
      const bubble = title.querySelector('.facets__bubble, .bubble');
      title.textContent = 'FILTER';
      if (bubble) {
        title.appendChild(document.createTextNode(' '));
        title.appendChild(bubble);
      }
    }

    const apply = drawer.querySelector('.facets__see-results');
    const clear = drawer.querySelector('.facets__clear-all');
    if (apply) apply.textContent = 'APPLY';
    if (clear) clear.textContent = 'CLEAR ALL';

    const sortLabel = drawer.querySelector('.sorting-filter .facets__label');
    if (sortLabel) sortLabel.textContent = 'SORT';

    const items = [...drawer.querySelectorAll('.facets__filters-wrapper .facets__item')];
    items.forEach((item, i) => {
      const label = item.querySelector('.facets__summary .facets__label');
      if (!label) return;
      const text = label.textContent.trim().toLowerCase();
      if (text.includes('size') || i === 0) label.textContent = 'SIZES';
      else if (text.includes('type') || text.includes('product type') || text.includes('category')) label.textContent = 'TYPE';
      else if (i === 1) label.textContent = 'TYPE';
    });

    drawer.querySelectorAll('.lf-filter-type-empty').forEach((el) => el.remove());
    const hasTypeFilter = items.some((item) => {
      const label = item.querySelector('.facets__summary .facets__label');
      return label?.textContent.trim() === 'TYPE';
    });

    if (!hasTypeFilter) {
      const scrollHint = drawer.querySelector('scroll-hint');
      const placeholder = document.createElement('div');
      placeholder.className = 'lf-filter-type-empty';
      placeholder.innerHTML = '<span class="lf-filter-type-empty__label">TYPE</span><p class="lf-filter-type-empty__text">NO TYPES FOUND.</p>';
      if (items[0]) items[0].after(placeholder);
      else if (scrollHint) scrollHint.prepend(placeholder);
    }

    drawer.querySelectorAll('.checkbox--disabled .checkbox__label-text').forEach((el) => {
      el.closest('.checkbox')?.classList.add('lf-filter-unavailable');
    });
  }

  /** @param {AbortSignal} signal */
  #initTyping(signal) {
    const run = () => {
      const grid = document.querySelector('.product-grid');
      if (grid) this.#runTyping(grid);
    };

    const runWhenReady = () => {
      if (window.TypingText) {
        run();
        return;
      }
      window.setTimeout(runWhenReady, 80);
    };

    runWhenReady();
    document.addEventListener('filter:update', () => window.setTimeout(run, 400), { signal });
    document.addEventListener('shopify:section:load', run, { signal });
  }

  /** @param {ParentNode} grid */
  #runTyping(grid) {
    if (!window.TypingText) return;

    const targets = new Set([
      ...grid.querySelectorAll('.product-card [data-typing-text], .product-card .text-block > *, .product-card .price'),
    ]);

    grid.querySelectorAll('.product-card .text-block').forEach((el) => {
      if (el instanceof HTMLElement && el.children.length === 0) targets.add(el);
    });

    [...targets].forEach((el, idx) => {
      if (!(el instanceof HTMLElement) || el.dataset.lfTyped === 'true') return;
      const text = el.dataset.typingText || el.textContent.trim();
      if (!text) return;
      el.dataset.typingText = text;
      el.dataset.lfTyped = 'true';
      window.TypingText.typeElement(el, {
        delay: 100 + idx * 75,
        speed: 58,
      });
    });
  }

  /** @param {string} productUrl */
  async open(productUrl) {
    const root = this.querySelector('#lf-qv-root');
    if (!root) return;

    const url = new URL(productUrl, window.location.origin);
    const handle = url.pathname.split('/products/')[1]?.split('/')[0];
    if (!handle) return;

    document.body.classList.add('lf-catalog-open');
    this.setAttribute('aria-hidden', 'false');
    this.#slideIndex = 0;
    root.innerHTML = '<div class="lf-qv lf-qv--loading"><span>Loading…</span></div>';

    try {
      const res = await fetch(`/products/${handle}?sections=${SECTION_ID}`);
      if (!res.ok) throw new Error('Failed to load product');
      const data = await res.json();
      root.innerHTML = data[SECTION_ID] || '';
      this.#qvAbort?.abort();
      this.#qvAbort = new AbortController();
      this.#bindQuickView(root, this.#qvAbort.signal);
    } catch {
      root.innerHTML = '<div class="lf-qv lf-qv--error"><p>Could not load product.</p></div>';
    }
  }

  close() {
    document.body.classList.remove('lf-catalog-open');
    this.setAttribute('aria-hidden', 'true');
    this.#qvAbort?.abort();
    const root = this.querySelector('#lf-qv-root');
    if (root) root.innerHTML = '';
  }

  /** @param {HTMLElement} root @param {AbortSignal} signal */
  #bindQuickView(root, signal) {
    const qv = root.querySelector('[data-lf-qv]');
    if (!qv) return;

    qv.querySelector('[data-lf-qv-close]')?.addEventListener('click', () => this.close(), { signal });
    qv.querySelector('[data-lf-size-chart-open]')?.addEventListener('click', () => this.#openSizeChart(qv), { signal });
    qv.querySelectorAll('[data-lf-size-chart-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.#closeSizeChart(), { signal });
    });
    qv.querySelectorAll('[data-lf-chart-unit]').forEach((btn) => {
      btn.addEventListener('click', () => this.#setChartUnit(qv, btn.getAttribute('data-lf-chart-unit') || 'in'), { signal });
    });

    const slides = /** @type {HTMLImageElement[]} */ ([...qv.querySelectorAll('.lf-qv__slide')]);
    if (slides.length > 1) {
      const showSlide = (index) => {
        this.#slideIndex = (index + slides.length) % slides.length;
        slides.forEach((s, i) => s.classList.toggle('is-active', i === this.#slideIndex));
        qv.querySelectorAll('[data-lf-qv-dot]').forEach((dot, i) => {
          dot.classList.toggle('is-active', i === this.#slideIndex);
        });
      };

      qv.querySelectorAll('[data-lf-qv-dot]').forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = Number(dot.getAttribute('data-lf-qv-dot'));
          if (!Number.isNaN(idx)) showSlide(idx);
        }, { signal });
      });

      qv.querySelector('[data-lf-qv-prev]')?.addEventListener('click', () => showSlide(this.#slideIndex - 1), { signal });
      qv.querySelector('[data-lf-qv-next]')?.addEventListener('click', () => showSlide(this.#slideIndex + 1), { signal });
    }

    const jsonEl = qv.querySelector('[data-lf-product-json]');
    if (!(jsonEl?.textContent)) return;

    /** @type {{ variants: Array<{id:number,available:boolean,price:number,options:string[],featured_image?:{src:string}}> }} */
    const product = JSON.parse(jsonEl.textContent);
    const variantInput = qv.querySelector('[data-lf-variant-input]');
    const priceEl = qv.querySelector('[data-lf-qv-price]');
    const submitBtn = qv.querySelector('[data-lf-qv-submit]');

    /** @type {string[]} */
    const selected = product.variants.find((v) => v.id === Number(variantInput?.getAttribute('value')))?.options?.slice() || product.variants[0]?.options?.slice() || [];

    const formatMoney = (cents) => {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
      return `$${(cents / 100).toFixed(2)}`;
    };

    const updateVariant = () => {
      const match = product.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      if (!match || !(variantInput instanceof HTMLInputElement)) return;

      variantInput.value = String(match.id);
      if (priceEl) priceEl.textContent = formatMoney(match.price);
      if (submitBtn instanceof HTMLButtonElement) {
        submitBtn.disabled = !match.available;
        submitBtn.textContent = match.available ? 'ADD' : 'SOLD OUT';
      }

      if (match.featured_image?.src) {
        const activeSlide = slides.find((s) => s.classList.contains('is-active')) || slides[0];
        if (activeSlide) activeSlide.src = match.featured_image.src;
      }
    };

    qv.querySelectorAll('[data-lf-qv-sizes] [data-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const optionIndex = Number(btn.closest('[data-lf-qv-sizes]')?.getAttribute('data-option-index'));
        const value = btn.getAttribute('data-value');
        if (Number.isNaN(optionIndex) || !value) return;

        selected[optionIndex] = value;
        btn.closest('[data-lf-qv-sizes]')?.querySelectorAll('[data-value]').forEach((b) => {
          b.classList.toggle('is-selected', b.getAttribute('data-value') === value);
        });
        updateVariant();
      }, { signal });
    });

    if (window.TypingText && priceEl instanceof HTMLElement) {
      const titleEl = qv.querySelector('.lf-qv__title');
      if (titleEl instanceof HTMLElement) {
        window.TypingText.typeElement(titleEl, { delay: 100, speed: 72 });
      }
      window.TypingText.typeElement(priceEl, { delay: 350, speed: 72 });
    }
  }

  /** @param {Element} root */
  #openSizeChart(root) {
    const chart = root.querySelector('[data-lf-size-chart]');
    if (!(chart instanceof HTMLElement)) return;

    this.#setChartUnit(root, 'in');
    chart.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lf-size-chart-open');
  }

  #closeSizeChart() {
    document.querySelectorAll('[data-lf-size-chart]').forEach((chart) => {
      chart.setAttribute('aria-hidden', 'true');
    });
    document.body.classList.remove('lf-size-chart-open');
  }

  /** @param {Element} root @param {string} unit */
  #setChartUnit(root, unit) {
    const isCm = unit === 'cm';

    root.querySelectorAll('[data-lf-chart-unit]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.getAttribute('data-lf-chart-unit') === unit);
    });

    root.querySelectorAll('[data-in]').forEach((cell) => {
      const inches = Number(cell.getAttribute('data-in'));
      if (Number.isNaN(inches)) return;
      cell.textContent = isCm ? String(Math.round(inches * 2.54)) : this.#formatInches(inches);
    });
  }

  /** @param {number} value */
  #formatInches(value) {
    const whole = Math.trunc(value);
    const fraction = Math.round((value - whole) * 8);
    const fractions = { 1: '⅛', 2: '¼', 3: '⅜', 4: '½', 5: '⅝', 6: '¾', 7: '⅞' };
    if (fraction === 0) return String(whole);
    if (fraction === 8) return String(whole + 1);
    return `${whole}${fractions[fraction] || ''}`;
  }
}

if (!customElements.get('lafortune-catalog-panel')) {
  customElements.define('lafortune-catalog-panel', LafortuneCatalogPanel);
}
